import { useQueryClient } from "@tanstack/react-query";
import { useCustomMutation } from "../custom-mutation";
import { post, del, get, put } from "../request-helpers";
import {
  DeleteLimeAdminEmailSuppression,
  DeleteLimeAdminEmailValidationBlockedDomain,
  DeleteLimeAdminEmailValidationConfirmedEmail,
  DeleteLimeAdminEmailValidationValidatedDomain,
  DeleteLimeAdminValkeyKey,
  GetLimeAdminEmailSuppression,
  PostLinkEracuniPartner,
  PostLimeAdminEmailValidationAllowedDomain,
  PostLimeAdminEmailValidationConfirmedEmail,
  PostLimeAdminEmailValidationValidatedDomain,
  DeleteAdminUserPin,
  PutLimeAdminClientUserEmail,
  PostLimeAdminClientUsers,
  GetLimeAdminAlertLogs,
  GetLimeAdminEmailValidationManualEntries,
  GetLimeAdminEmailValidationAttempts,
  GetLimeAdminNotificationLimitCurrentHour,
  GetLimeAdminNotificationLimitMonthly,
  GetLimeAdminBannerSettings,
  GetLimeAdminBannerSettingsPreview,
  GetLimeAdminRateLimitConfig,
  PutLimeAdminBannerSettings,
  PutLimeAdminRateLimitConfig,
  GetLimeAdminRateLimitAnalytics,
  GetLimeAdminRateLimitHistory,
  DeleteLimeAdminRateLimitBlockedKey,
  GetLimeAdminNotificationLimitWarnings,
  GetLimeAdminValkeyKeys,
  GetLimeAdminWebhookDeliveries,
  GetLimeAdminWebhookEndpoints,
  GetLimeAdminWebhookStats,
  PostLimeAdminDisableWebhookEndpoint,
  GetLimeAdminSmsPause,
  PutLimeAdminSmsPause,
  GetLimeAdminSmsDailyLimit,
  PutLimeAdminSmsDailyLimit,
} from "@/server-types";
import { useCustomQuery } from "../custom-query";

export const useLimeAdminClientUsers = (
  clientId: number,
  body: PostLimeAdminClientUsers["body"] = {},
  disabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["lime-admin-client-users", clientId, body],
    queryFn: () =>
      post<PostLimeAdminClientUsers>(
        `/lime-admin/client/${clientId}/user`,
        body,
      ),
    enabled: !disabled,
  });
};

export const usePostLinkEracuniPartner = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: PostLinkEracuniPartner["params"];
      body: PostLinkEracuniPartner["body"];
    }) =>
      post<PostLinkEracuniPartner>(
        `/lime-admin/client/${props.params.clientId}/eracuni/link`,
        props.body,
      ),
  });
};

export const useDeleteAdminUserPin = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: DeleteAdminUserPin["params"];
      body: DeleteAdminUserPin["body"];
    }) =>
      del<DeleteAdminUserPin>(
        `/lime-admin/user/${props.params.userId}/pin`,
        props.body,
      ),
  });
};

export const usePutLimeAdminClientUserEmail = (
  options: { onSuccess?: () => void } = {},
) => {
  return useCustomMutation({
    mutationFn: (props: {
      params: PutLimeAdminClientUserEmail["params"];
      body: PutLimeAdminClientUserEmail["body"];
    }) =>
      put<PutLimeAdminClientUserEmail>(
        `/lime-admin/client/${props.params.clientId}/user/${props.params.userId}/email`,
        props.body,
      ),
    onSuccess: options.onSuccess,
  });
};

export const useGetLimeAdminRateLimitConfig = (props: {
  query: GetLimeAdminRateLimitConfig["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminRateLimitConfig", props],
    queryFn: () =>
      get<GetLimeAdminRateLimitConfig>(
        `/lime-admin/rate-limit/config`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const usePutLimeAdminRateLimitConfig = () => {
  return useCustomMutation({
    mutationFn: (props: { body: PutLimeAdminRateLimitConfig["body"] }) =>
      put<PutLimeAdminRateLimitConfig>(
        `/lime-admin/rate-limit/config`,
        props.body,
      ),
  });
};

export const useGetLimeAdminSmsPause = () => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminSmsPause"],
    queryFn: () => get<GetLimeAdminSmsPause>(`/lime-admin/sms-pause`),
  });
};

export const usePutLimeAdminSmsPause = () => {
  return useCustomMutation({
    mutationFn: (props: { body: PutLimeAdminSmsPause["body"] }) =>
      put<PutLimeAdminSmsPause>(`/lime-admin/sms-pause`, props.body),
  });
};

export const useGetLimeAdminSmsDailyLimit = () => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminSmsDailyLimit"],
    queryFn: () =>
      get<GetLimeAdminSmsDailyLimit>(`/lime-admin/sms-daily-limit`),
  });
};

export const usePutLimeAdminSmsDailyLimit = () => {
  return useCustomMutation({
    mutationFn: (props: { body: PutLimeAdminSmsDailyLimit["body"] }) =>
      put<PutLimeAdminSmsDailyLimit>(`/lime-admin/sms-daily-limit`, props.body),
  });
};

export const useGetLimeAdminBannerSettings = () => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminBannerSettings"],
    queryFn: () =>
      get<GetLimeAdminBannerSettings>(`/lime-admin/banner-settings`),
  });
};

export const usePutLimeAdminBannerSettings = () => {
  return useCustomMutation({
    mutationFn: (props: { body: PutLimeAdminBannerSettings["body"] }) =>
      put<PutLimeAdminBannerSettings>(
        `/lime-admin/banner-settings`,
        props.body,
      ),
  });
};

export const useGetLimeAdminBannerSettingsPreview = () => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminBannerSettingsPreview"],
    queryFn: () =>
      get<GetLimeAdminBannerSettingsPreview>(
        `/lime-admin/banner-settings/preview`,
      ),
  });
};

export const useGetLimeAdminRateLimitAnalytics = (props: {
  query: GetLimeAdminRateLimitAnalytics["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminRateLimitAnalytics", props],
    queryFn: () =>
      get<GetLimeAdminRateLimitAnalytics>(
        `/lime-admin/rate-limit/analytics`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useGetLimeAdminRateLimitHistory = (props: {
  query: GetLimeAdminRateLimitHistory["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminRateLimitHistory", props],
    queryFn: () =>
      get<GetLimeAdminRateLimitHistory>(
        `/lime-admin/rate-limit/history`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useDeleteLimeAdminRateLimitBlockedKey = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: { body: DeleteLimeAdminRateLimitBlockedKey["body"] }) =>
      del<DeleteLimeAdminRateLimitBlockedKey>(
        `/lime-admin/rate-limit/blocked-key`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminRateLimitAnalytics"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminRateLimitHistory"],
      });
    },
  });
};

export const useGetLimeAdminValkeyKeys = (props: {
  query: GetLimeAdminValkeyKeys["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminValkeyKeys", props],
    queryFn: () =>
      get<GetLimeAdminValkeyKeys>(`/lime-admin/valkey/keys`, props.query),
    enabled: !props.disabled,
  });
};

export const useDeleteLimeAdminValkeyKey = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: { body: DeleteLimeAdminValkeyKey["body"] }) =>
      del<DeleteLimeAdminValkeyKey>(`/lime-admin/valkey/key`, props.body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminValkeyKeys"],
      });
    },
  });
};

export const useGetLimeAdminNotificationLimitWarnings = (props?: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminNotificationLimitWarnings"],
    queryFn: () =>
      get<GetLimeAdminNotificationLimitWarnings>(
        `/lime-admin/notification-limits/warnings`,
      ),
    enabled: !props?.disabled,
  });
};

export const useGetLimeAdminNotificationLimitMonthly = (props?: {
  query?: GetLimeAdminNotificationLimitMonthly["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminNotificationLimitMonthly", props],
    queryFn: () =>
      get<GetLimeAdminNotificationLimitMonthly>(
        `/lime-admin/notification-limits/monthly`,
        props?.query,
      ),
    enabled: !props?.disabled,
  });
};

export const useGetLimeAdminNotificationLimitCurrentHour = (props?: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminNotificationLimitCurrentHour"],
    queryFn: () =>
      get<GetLimeAdminNotificationLimitCurrentHour>(
        `/lime-admin/notification-limits/current-hour`,
      ),
    enabled: !props?.disabled,
  });
};

export const useGetLimeAdminEmailValidationAttempts = (props: {
  query: GetLimeAdminEmailValidationAttempts["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminEmailValidationAttempts", props],
    queryFn: () =>
      get<GetLimeAdminEmailValidationAttempts>(
        `/lime-admin/email-validation-attempts`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useGetLimeAdminEmailValidationManualEntries = (props?: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
    queryFn: () =>
      get<GetLimeAdminEmailValidationManualEntries>(
        `/lime-admin/email-validation-attempts/manual-entries`,
      ),
    enabled: !props?.disabled,
  });
};

export const useDeleteLimeAdminEmailValidationBlockedDomain = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: DeleteLimeAdminEmailValidationBlockedDomain["body"];
    }) =>
      del<DeleteLimeAdminEmailValidationBlockedDomain>(
        `/lime-admin/email-validation-attempts/blocked-domain`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationAttempts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const usePostLimeAdminEmailValidationAllowedDomain = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: PostLimeAdminEmailValidationAllowedDomain["body"];
    }) =>
      post<PostLimeAdminEmailValidationAllowedDomain>(
        `/lime-admin/email-validation-attempts/allowed-domain`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationAttempts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const usePostLimeAdminEmailValidationValidatedDomain = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: PostLimeAdminEmailValidationValidatedDomain["body"];
    }) =>
      post<PostLimeAdminEmailValidationValidatedDomain>(
        `/lime-admin/email-validation-attempts/validated-domain`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationAttempts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const useDeleteLimeAdminEmailValidationValidatedDomain = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: DeleteLimeAdminEmailValidationValidatedDomain["body"];
    }) =>
      del<DeleteLimeAdminEmailValidationValidatedDomain>(
        `/lime-admin/email-validation-attempts/validated-domain`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationAttempts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const usePostLimeAdminEmailValidationConfirmedEmail = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: PostLimeAdminEmailValidationConfirmedEmail["body"];
    }) =>
      post<PostLimeAdminEmailValidationConfirmedEmail>(
        `/lime-admin/email-validation-attempts/confirmed-email`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const useDeleteLimeAdminEmailValidationConfirmedEmail = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      body: DeleteLimeAdminEmailValidationConfirmedEmail["body"];
    }) =>
      del<DeleteLimeAdminEmailValidationConfirmedEmail>(
        `/lime-admin/email-validation-attempts/confirmed-email`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailValidationManualEntries"],
      });
    },
  });
};

export const useGetLimeAdminEmailSuppression = (props: {
  query: GetLimeAdminEmailSuppression["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminEmailSuppression", props],
    queryFn: () =>
      get<GetLimeAdminEmailSuppression>(
        `/lime-admin/email-suppression`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useDeleteLimeAdminEmailSuppression = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: { body: DeleteLimeAdminEmailSuppression["body"] }) =>
      del<DeleteLimeAdminEmailSuppression>(
        `/lime-admin/email-suppression`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminEmailSuppression"],
      });
    },
  });
};

export const useGetLimeAdminAlertLogs = (props: {
  query: GetLimeAdminAlertLogs["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminAlertLogs", props],
    queryFn: () =>
      get<GetLimeAdminAlertLogs>(`/lime-admin/alert-logs`, props.query),
    enabled: !props.disabled,
  });
};

export const useGetLimeAdminWebhookEndpoints = (props: {
  query: GetLimeAdminWebhookEndpoints["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminWebhookEndpoints", props],
    queryFn: () =>
      get<GetLimeAdminWebhookEndpoints>(
        `/lime-admin/webhooks/endpoints`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useGetLimeAdminWebhookDeliveries = (props: {
  query: GetLimeAdminWebhookDeliveries["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminWebhookDeliveries", props],
    queryFn: () =>
      get<GetLimeAdminWebhookDeliveries>(
        `/lime-admin/webhooks/deliveries`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const useGetLimeAdminWebhookStats = (props: {
  query: GetLimeAdminWebhookStats["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetLimeAdminWebhookStats", props],
    queryFn: () =>
      get<GetLimeAdminWebhookStats>(`/lime-admin/webhooks/stats`, props.query),
    enabled: !props.disabled,
  });
};

export const useDisableLimeAdminWebhookEndpoint = () => {
  const queryClient = useQueryClient();

  return useCustomMutation({
    mutationFn: (props: {
      webhookEndpointId: number;
      body: PostLimeAdminDisableWebhookEndpoint["body"];
    }) =>
      post<PostLimeAdminDisableWebhookEndpoint>(
        `/lime-admin/webhooks/${props.webhookEndpointId}/disable`,
        props.body,
      ),
    displayNotification: true,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminWebhookEndpoints"],
      });
      queryClient.invalidateQueries({
        queryKey: ["useGetLimeAdminWebhookStats"],
      });
    },
  });
};
